if (!customElements.get("hulii-v2-carousel")) {
  customElements.define(
    "hulii-v2-carousel",
    class extends HTMLElement {
      connectedCallback() {
        this.controller = new AbortController();
        const events = { signal: this.controller.signal };
        this.slides = [...this.querySelectorAll("[data-slide]")];
        this.index = 0;
        this.motion = matchMedia("(prefers-reduced-motion: reduce)");
        this.playing = this.dataset.autoplay === "true" && !this.motion.matches;
        this.controls = this.querySelector(".hv2-carousel-controls");
        if (this.slides.length < 2) return;
        this.controls.hidden = false;
        this.classList.add("is-enhanced");
        this.show(0, false);
        this.querySelector("[data-prev]").addEventListener(
          "click",
          (event) => {
            this.stop();
            this.show(this.index - 1, event.detail !== 0);
          },
          events,
        );
        this.querySelector("[data-next]").addEventListener(
          "click",
          (event) => {
            this.stop();
            this.show(this.index + 1, event.detail !== 0);
          },
          events,
        );
        this.querySelector("[data-play]").addEventListener(
          "click",
          () => {
            this.playing = !this.playing;
            this.schedule();
          },
          events,
        );
        this.addEventListener(
          "pointerenter",
          () => {
            this.hovered = true;
            clearTimeout(this.timer);
          },
          events,
        );
        this.addEventListener(
          "pointerleave",
          () => {
            this.hovered = false;
            this.schedule();
          },
          events,
        );
        // Focus pauses autoplay until the visitor explicitly restarts it.
        this.addEventListener(
          "focusin",
          (event) => {
            if (!event.target.matches("[data-play]")) this.stop();
          },
          events,
        );
        document.addEventListener(
          "visibilitychange",
          () => this.schedule(),
          events,
        );
        this.motion.addEventListener(
          "change",
          () => {
            if (this.motion.matches) this.stop();
          },
          events,
        );
        document.addEventListener(
          "shopify:block:select",
          (event) => {
            const index = this.slides.findIndex(
              (slide) => slide === event.target || slide.contains(event.target),
            );
            if (index >= 0) {
              this.stop();
              this.show(index);
            }
          },
          events,
        );
        this.schedule();
      }
      show(index, animate = true) {
        const previous = this.slides[this.index];
        this.transition?.cancel();
        this.index = (index + this.slides.length) % this.slides.length;
        const current = this.slides[this.index];
        const crossfade =
          animate && !this.motion.matches && previous !== current;
        this.slides.forEach((slide) => {
          const active = slide === current;
          slide.hidden = !active && !(crossfade && slide === previous);
          slide.inert = !active;
          slide.setAttribute("aria-hidden", String(!active));
          slide.style.zIndex = active ? "1" : "0";
        });
        if (crossfade) {
          const transition = current.animate([{ opacity: 0 }, { opacity: 1 }], {
            duration: 360,
            easing: "cubic-bezier(0.23, 1, 0.32, 1)",
          });
          this.transition = transition;
          transition.onfinish = () => {
            previous.hidden = true;
          };
        }
        // Warm the next image before autoplay reaches it.
        const nextImage =
          this.slides[(this.index + 1) % this.slides.length].querySelector(
            "img",
          );
        if (nextImage) nextImage.loading = "eager";
        this.querySelector("[data-count]").textContent =
          `${this.index + 1} / ${this.slides.length}`;
      }
      stop() {
        this.playing = false;
        this.schedule();
      }
      schedule() {
        clearTimeout(this.timer);
        const button = this.querySelector("[data-play]");
        button.setAttribute("aria-pressed", String(this.playing));
        button.textContent = this.playing ? "Ⅱ" : "▷";
        this.querySelector("[data-count]").setAttribute(
          "aria-live",
          this.playing ? "off" : "polite",
        );
        if (this.playing && !document.hidden && !this.hovered) {
          this.timer = setTimeout(
            () => {
              this.show(this.index + 1);
              this.schedule();
            },
            Number(this.dataset.interval) || 7000,
          );
        }
      }
      disconnectedCallback() {
        clearTimeout(this.timer);
        this.transition?.cancel();
        this.controller?.abort();
      }
    },
  );
}

if (!customElements.get("hulii-v2-product")) {
  customElements.define(
    "hulii-v2-product",
    class extends HTMLElement {
      connectedCallback() {
        this.controller = new AbortController();
        const events = { signal: this.controller.signal };
        this.form = this.querySelector("form");
        this.price = this.querySelector("[data-price]");
        this.startingPrice = this.price.textContent;
        this.image = this.querySelector(".hv2-card-image img");
        this.originalImage = this.image
          ? { src: this.image.src, srcset: this.image.srcset }
          : null;
        this.detailsLink = this.querySelector("[data-details]");
        this.addButton = this.querySelector("[data-add]");
        const data = this.querySelector("[data-variants]");
        if (data && this.form) {
          this.variants = JSON.parse(data.textContent);
          this.selects = [...this.querySelectorAll("[data-option-index]")];
          this.variantInput = this.form.querySelector('[name="id"]');
          this.form.querySelector(".hv2-variant-fallback").hidden = true;
          this.form.querySelector(".hv2-nojs-add").hidden = true;
          this.form.querySelector("[data-option-fields]").hidden = false;
          this.selects.forEach((select, index) => {
            select
              .closest("fieldset")
              .querySelectorAll("[data-option-choice]")
              .forEach((button) => {
                button.addEventListener(
                  "click",
                  () => {
                    if (select.value === button.dataset.optionChoice) return;
                    select.value = button.dataset.optionChoice;
                    select.dispatchEvent(new Event("change"));
                  },
                  events,
                );
              });
            select.addEventListener(
              "change",
              () => {
                // Reset dependent choices; never silently pick a different color, grade or size.
                this.selects.slice(index + 1).forEach((next) => {
                  next.value = "";
                });
                this.update();
              },
              events,
            );
          });
          this.addButton.addEventListener(
            "click",
            () => {
              if (this.variantInput.value) this.form.requestSubmit();
            },
            events,
          );
          this.form.addEventListener(
            "submit",
            (event) => {
              const cart = document.querySelector("hulii-v2-cart");
              if (
                typeof cart?.add !== "function" ||
                typeof cart.dialog?.showModal !== "function"
              )
                return;
              event.preventDefault();
              event.stopPropagation();
              // Close quick view first so the cart is the only active modal.
              if (this.dialog?.open) this.dialog.close();
              cart.add(this.form, this.addButton);
            },
            events,
          );
          this.update();
        }
        this.dialog = this.querySelector("dialog");
        this.openButton = this.querySelector("[data-quick-open]");
        if (this.dialog && typeof this.dialog.showModal === "function") {
          this.openButton.hidden = false;
          this.openButton.addEventListener(
            "click",
            () => this.openQuickView(),
            events,
          );
          this.querySelector("[data-quick-close]").addEventListener(
            "click",
            () => this.dialog.close(),
            events,
          );
          this.dialog.addEventListener(
            "click",
            (event) => {
              if (event.target === this.dialog) this.dialog.close();
            },
            events,
          );
          this.dialog.addEventListener(
            "close",
            () => this.restoreCard(),
            events,
          );
        }
      }
      update() {
        this.selects.forEach((select, index) => {
          const preceding = this.selects
            .slice(0, index)
            .map((field) => field.value);
          select.disabled = preceding.some((value) => !value);
          [...select.options].forEach((option) => {
            if (!option.value) return;
            const available = this.variants.some(
              (variant) =>
                variant.available &&
                variant.options[index] === option.value &&
                preceding.every(
                  (value, i) => !value || variant.options[i] === value,
                ),
            );
            option.disabled = !available;
            option.hidden = !available;
          });
          const group = select.closest("fieldset");
          group.querySelector("[data-option-selected]").textContent =
            select.value ? `: ${select.value}` : "";
          group.querySelectorAll("[data-option-choice]").forEach((button) => {
            const option = [...select.options].find(
              (item) => item.value === button.dataset.optionChoice,
            );
            button.hidden = !option || option.hidden;
            button.disabled = select.disabled || !option || option.disabled;
            button.setAttribute(
              "aria-pressed",
              String(select.value === button.dataset.optionChoice),
            );
          });
        });
        const selected = this.selects.map((select) => select.value);
        const variant = this.variants.find(
          (item) =>
            item.available &&
            item.options.every((value, i) => selected[i] === value),
        );
        this.variantInput.value = variant ? String(variant.id) : "";
        this.price.textContent = variant ? variant.price : this.startingPrice;
        this.detailsLink.hidden = Boolean(variant);
        this.addButton.hidden = !variant;
        if (this.image && this.originalImage) {
          this.image.src = variant?.image || this.originalImage.src;
          this.image.srcset = variant?.image ? "" : this.originalImage.srcset;
        }
        const quickImage = this.querySelector(".hv2-quick-image");
        if (quickImage && this.image) {
          quickImage.src = this.image.src;
          quickImage.srcset = this.image.srcset;
        }
        this.querySelector("[data-quick-price]")?.replaceChildren(
          document.createTextNode(this.price.textContent),
        );
      }
      openQuickView() {
        const options = this.querySelector(".hv2-options");
        this.optionsWereOpen = options?.open;
        if (options) {
          this.dialog.querySelector("[data-quick-options]").append(options);
          options.open = true;
        }
        const actions = this.dialog.querySelector("[data-quick-actions]");
        actions.append(this.detailsLink);
        if (this.addButton) actions.append(this.addButton);
        this.dialog.querySelector("[data-quick-price]").textContent =
          this.price.textContent;
        this.dialog.showModal();
      }
      restoreCard() {
        const options = this.dialog.querySelector(".hv2-options");
        if (options) {
          this.querySelector("[data-options-home]").append(options);
          options.open = this.optionsWereOpen;
        }
        this.insertBefore(this.detailsLink, this.dialog);
        if (this.addButton) this.insertBefore(this.addButton, this.dialog);
        if (this.isConnected) this.openButton.focus();
      }
      connectedMoveCallback() {
        /* Keep selected variants when the catalog reorders cards. */
      }
      disconnectedCallback() {
        this.controller?.abort();
        if (this.dialog?.open) this.dialog.close();
      }
    },
  );
}

// Progressive enhancement: every grade stays readable without JavaScript.
if (!customElements.get("hulii-v2-grades")) {
  customElements.define(
    "hulii-v2-grades",
    class extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        const options = { signal: this.controller.signal };
        const buttons = [...this.querySelectorAll("[data-grade-button]")];
        const panels = [...this.querySelectorAll("[data-grade-panel]")];
        const show = (index) => {
          panels.forEach((panel, i) => {
            panel.hidden = i !== index;
          });
          buttons.forEach((button, i) =>
            button.setAttribute("aria-pressed", String(i === index)),
          );
        };
        if (!panels.length) return;
        this.querySelector(".hv2-grade-controls").hidden = panels.length < 2;
        buttons.forEach((button, index) =>
          button.addEventListener("click", () => show(index), options),
        );
        // Selecting a block in Shopify must reveal that grade, without stealing focus.
        this.addEventListener(
          "shopify:block:select",
          (event) => {
            const index = panels.findIndex(
              (panel) => panel.dataset.blockId === event.detail?.blockId,
            );
            if (index >= 0) show(index);
          },
          options,
        );
        show(0);
      }
      disconnectedCallback() {
        this.controller?.abort();
      }
    },
  );
}

if (!customElements.get("hulii-v2-catalog")) {
  customElements.define(
    "hulii-v2-catalog",
    class extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        const events = { signal: this.controller.signal };
        const grid = this.querySelector(".hv2-product-grid");
        const items = [...this.querySelectorAll("[data-catalog-item]")];
        const pills = [...this.querySelectorAll("[data-category]")];
        const query = this.querySelector("[data-catalog-query]");
        const budget = this.querySelector("[data-catalog-budget]");
        const sort = this.querySelector("[data-catalog-sort]");
        const normalize = (value) =>
          value.normalize("NFKD").replace(/\p{M}/gu, "").toLocaleLowerCase();
        let category = "all";
        const update = () => {
          const words = normalize(query.value)
            .trim()
            .split(/\s+/)
            .filter(Boolean);
          const matching = items.filter(
            (item) =>
              (category === "all" ||
                item.dataset.categories.split(/\s+/).includes(category)) &&
              words.every((word) =>
                normalize(item.dataset.title).includes(word),
              ) &&
              (!budget.value ||
                Number(item.dataset.startingPrice) < Number(budget.value)),
          );
          if (sort.value === "asc" || sort.value === "desc")
            matching.sort(
              (a, b) =>
                (Number(a.dataset.startingPrice) -
                  Number(b.dataset.startingPrice)) *
                (sort.value === "asc" ? 1 : -1),
            );
          if (sort.value === "name")
            matching.sort((a, b) =>
              a.dataset.title.localeCompare(
                b.dataset.title,
                document.documentElement.lang || "nb",
              ),
            );
          const visible = matching.slice(0, Number(this.dataset.limit) || 8);
          items.forEach((item) => {
            item.hidden = !visible.includes(item);
          });
          // moveBefore preserves custom-element state and keyboard order when sorting.
          [
            ...visible,
            ...items.filter((item) => !visible.includes(item)),
          ].forEach((item, index) => {
            if (grid.children[index] === item) return;
            if (grid.moveBefore)
              grid.moveBefore(item, grid.children[index] || null);
            else grid.insertBefore(item, grid.children[index] || null);
          });
          pills.forEach((pill) =>
            pill.setAttribute(
              "aria-pressed",
              String(pill.dataset.category === category),
            ),
          );
          this.querySelector("[data-catalog-count]").textContent =
            `${visible.length} ${this.dataset.countLabel || ""}`;
          this.querySelector("[data-catalog-empty]").hidden =
            matching.length !== 0;
        };
        pills.forEach((pill) =>
          pill.addEventListener(
            "click",
            () => {
              category = pill.dataset.category;
              update();
            },
            events,
          ),
        );
        query.addEventListener("input", update, events);
        budget.addEventListener("change", update, events);
        sort.addEventListener("change", update, events);
        this.querySelector("[data-catalog-reset]").addEventListener(
          "click",
          () => {
            category = "all";
            query.value = "";
            budget.value = "";
            sort.value = "featured";
            update();
            pills[0].focus();
          },
          events,
        );
        this.addEventListener(
          "shopify:block:select",
          (event) => {
            const pill = event.target.closest("[data-category]");
            if (pill) {
              category = pill.dataset.category;
              update();
            }
          },
          events,
        );
        this.querySelector("[data-catalog-controls]").hidden = false;
        update();
      }
      disconnectedCallback() {
        this.controller?.abort();
      }
    },
  );
}
